import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Save, Lock, Download, Link2, UploadCloud, X, Globe } from "lucide-react";
import { toast } from "sonner";
import { expertApi, fileUrlFor, formatApiError, API } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import Logo from "@/components/Logo";
import AvailabilityGrid, { scheduleToCells, cellsToSchedule } from "@/components/AvailabilityGrid";

const inputCls = "field-input";
const TIMEZONES = ["UTC", "Europe/London", "Europe/Paris", "Africa/Cairo", "Asia/Dubai", "Asia/Riyadh", "Asia/Amman", "America/New_York"];
const DURATIONS = [30, 60, 90, 120];

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800", confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-sky-100 text-sky-800", cancelled: "bg-red-100 text-red-700", "no-show": "bg-slate-200 text-slate-600",
  unpaid: "bg-amber-100 text-amber-800", paid: "bg-emerald-100 text-emerald-800", refunded: "bg-slate-200 text-slate-600",
};

const Badge = ({ value }) => (
  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${STATUS_CLS[value] || "bg-slate-100 text-slate-600"}`}>{value}</span>
);

function LangToggle({ className }) {
  const { lang, setLang } = useLanguage();
  return (
    <button
      type="button"
      data-testid="portal-language-toggle"
      onClick={() => setLang(lang === "en" ? "ar" : "en")}
      className={className || "absolute end-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-gold"}
    >
      <Globe className="h-3.5 w-3.5" /> {lang === "en" ? "العربية" : "EN"}
    </button>
  );
}

function Login({ onSuccess }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login");
  const [resetSent, setResetSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await expertApi.post("/expert-auth/login", { email, password });
      localStorage.setItem("stratedge-expert-token", data.token);
      onSuccess(data.token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await expertApi.post("/expert-auth/forgot-password", { email });
      setResetSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-navy-950 px-4" data-testid="expert-login-page">
      <LangToggle />
      <div className="w-full max-w-md rounded-lg bg-white p-10 shadow-2xl">
        <Logo />
        <h1 className="mt-8 font-display text-3xl font-semibold text-navy">{t("portal.expertTitle")}</h1>
        {view === "login" ? (
          <>
            <p className="mt-1 text-sm text-slate-500">{t("portal.expertSub")}</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="expert-login-form">
              <div>
                <label htmlFor="expert-email" className="field-label">{t("form.email")}</label>
                <input id="expert-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="expert-email-input" autoComplete="username" />
              </div>
              <div>
                <label htmlFor="expert-password" className="field-label">{t("portal.password")}</label>
                <input id="expert-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="expert-password-input" autoComplete="current-password" />
              </div>
              {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="expert-login-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="expert-login-submit"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 text-sm font-bold text-white hover:bg-navy-800 disabled:opacity-60">
                <Lock className="h-4 w-4" /> {loading ? t("portal.signingIn") : t("portal.signin")}
              </button>
              <button type="button" onClick={() => { setView("forgot"); setError(""); }} data-testid="expert-forgot-link"
                className="w-full text-center text-xs font-semibold text-gold-hover hover:underline">
                {t("portal.forgot")}
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-slate-500">{t("portal.forgot")}</p>
            {resetSent ? (
              <div className="mt-8" data-testid="expert-reset-sent">
                <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800" role="status">{t("portal.resetSent")}</p>
                <p className="mt-4 text-xs text-slate-500">{t("portal.support")} <a href="mailto:info@stratedgeuk.com" className="font-semibold text-gold-hover">info@stratedgeuk.com</a></p>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="mt-8 space-y-5" data-testid="expert-forgot-form">
                <div>
                  <label htmlFor="expert-forgot-email" className="field-label">{t("form.email")}</label>
                  <input id="expert-forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="expert-forgot-email-input" />
                </div>
                {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
                <button type="submit" disabled={loading} data-testid="expert-forgot-submit"
                  className="w-full rounded-full bg-navy py-3.5 text-sm font-bold text-white hover:bg-navy-800 disabled:opacity-60">
                  {loading ? t("portal.signingIn") : t("portal.sendReset")}
                </button>
              </form>
            )}
            <button type="button" onClick={() => { setView("login"); setResetSent(false); setError(""); }} data-testid="expert-back-to-login"
              className="mt-4 w-full text-center text-xs font-semibold text-slate-500 hover:underline">
              {t("portal.backToLogin")}
            </button>
          </>
        )}
        <p className="mt-6 border-t border-slate-100 pt-4 text-center text-xs text-slate-400">
          <Link to="/" data-testid="expert-back-to-site" className="font-semibold text-gold-hover hover:underline">{t("portal.backToSite")}</Link>
          {" · "}{t("portal.support")} <a href="mailto:info@stratedgeuk.com" className="font-semibold text-gold-hover">info@stratedgeuk.com</a>
        </p>
      </div>
    </div>
  );
}

function ResetPassword({ email, token, onDone }) {
  const { t } = useLanguage();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await expertApi.post("/expert-auth/reset-password", { email, token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-navy-950 px-4" data-testid="expert-reset-page">
      <LangToggle />
      <div className="w-full max-w-md rounded-lg bg-white p-10 shadow-2xl">
        <Logo />
        <h1 className="mt-8 font-display text-3xl font-semibold text-navy">{t("portal.resetTitle")}</h1>
        {done ? (
          <div className="mt-8" data-testid="expert-reset-done">
            <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800" role="status">{t("portal.resetDone")}</p>
            <button onClick={onDone} data-testid="expert-reset-to-login" className="mt-5 w-full rounded-full bg-navy py-3.5 text-sm font-bold text-white hover:bg-navy-800">
              {t("portal.backToLogin")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="expert-reset-form">
            <div>
              <label htmlFor="expert-new-password" className="field-label">{t("portal.newPassword")}</label>
              <input id="expert-new-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="expert-new-password-input" autoComplete="new-password" />
            </div>
            {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="expert-reset-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="expert-reset-submit"
              className="w-full rounded-full bg-gold py-3.5 text-sm font-bold text-navy hover:bg-gold-hover disabled:opacity-60">
              {loading ? t("portal.signingIn") : t("portal.resetTitle")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function BookingRow({ b, onSaved }) {
  const [link, setLink] = useState(b.meeting_link || "");
  const [saving, setSaving] = useState(false);

  const saveLink = async () => {
    setSaving(true);
    try {
      await expertApi.patch(`/expert-portal/bookings/${b.id}/meeting-link`, { meeting_link: link });
      toast.success("Meeting link saved and emailed");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const markCompleted = async () => {
    try {
      await expertApi.patch(`/expert-portal/bookings/${b.id}/status`, { booking_status: "completed" });
      toast.success("Session marked completed");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <tr className="border-b border-slate-50 align-top" data-testid={`expert-booking-row-${b.booking_id}`}>
      <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{b.booking_id}
        <span className="block text-[10px] font-semibold uppercase text-slate-400">{b.consultation_type || "expert"}</span>
      </td>
      <td className="px-4 py-3 text-slate-700">{b.client_name}<span className="block text-xs text-slate-400">{b.topic}</span></td>
      <td className="px-4 py-3 text-slate-600">{b.date} {b.time}<span className="block text-[10px] text-slate-400">{b.timezone}</span></td>
      <td className="px-4 py-3 text-slate-600">{b.duration} min</td>
      <td className="px-4 py-3 font-bold text-navy">USD {Number(b.total_amount).toLocaleString()}</td>
      <td className="px-4 py-3"><Badge value={b.booking_status} /><span className="mt-1 block"><Badge value={b.payment_status} /></span>
        {b.booking_status === "confirmed" && (
          <button onClick={markCompleted} data-testid={`mark-completed-${b.booking_id}`}
            className="mt-1.5 block rounded-full bg-sky-100 px-2.5 py-1 text-[10px] font-bold uppercase text-sky-800 hover:bg-sky-200">
            Mark Completed
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        {b.booking_status === "confirmed" && (
          <div className="flex items-center gap-1.5">
            <input className="w-40 rounded border border-slate-200 px-2 py-1 text-xs" placeholder="https://meet..." value={link}
              onChange={(e) => setLink(e.target.value)} data-testid={`meeting-link-input-${b.booking_id}`} />
            <button onClick={saveLink} disabled={saving} data-testid={`meeting-link-save-${b.booking_id}`}
              className="rounded-full bg-gold p-1.5 text-navy hover:bg-gold-hover" aria-label="Save meeting link">
              <Link2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {b.files?.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {b.files.map((f, i) => (
              <a key={i} href={fileUrlFor(f.path, "expert")} data-testid={`booking-file-${b.booking_id}-${i}`}
                className="flex items-center gap-1 text-[11px] font-semibold text-gold-hover hover:underline">
                <Download className="h-3 w-3" /> {f.filename}
              </a>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

const PROFILE_TEXT = [
  ["full_name", "Full name"], ["job_title", "Professional title"], ["country", "Country"], ["city", "City"],
  ["specialization", "Specialization"], ["highest_degree", "Highest academic degree"], ["university", "University"],
  ["years_experience", "Years of experience", "number"], ["linkedin", "LinkedIn URL"],
  ["google_scholar", "Google Scholar URL"], ["orcid", "ORCID URL"],
];
const PROFILE_LISTS = [
  ["expertise_areas", "Areas of expertise"], ["key_skills", "Key skills"],
  ["languages", "Languages"], ["work_regions", "Countries / regions you can work in"],
];
const PROFILE_AREAS = [
  ["bio", "Professional biography"], ["qualifications", "Academic qualifications"],
  ["training_experience", "Training experience"], ["consulting_experience", "Consulting experience"],
  ["sector_experience", "Industry / sector experience"],
];
const RESEARCH_TEXT = [["academic_discipline", "Academic discipline"], ["supervision_experience", "Research supervision experience"]];
const RESEARCH_LISTS = [["research_areas", "Research areas"], ["research_methodologies", "Research methodologies"]];

function ProfileEditor({ me, onSaved }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [photoTick, setPhotoTick] = useState(0);

  useEffect(() => {
    expertApi.get("/expert-portal/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  if (!profile) return null;
  const offersResearch = ["research", "both"].includes(profile.consultation_type);
  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });
  const photoSrc = profile.has_uploaded_photo ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_endpoint}?t=${photoTick}` : profile.photo_url;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      [...PROFILE_LISTS, ...RESEARCH_LISTS].forEach(([k]) => {
        payload[k] = String(profile[k] ?? "").split(",").map((s) => s.trim()).filter(Boolean);
      });
      payload.years_experience = parseInt(payload.years_experience, 10) || 0;
      await expertApi.put("/expert-portal/profile", payload);
      toast.success("Profile saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await expertApi.post("/expert-portal/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      setProfile({ ...profile, has_uploaded_photo: true });
      setPhotoTick(Date.now());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm" data-testid="profile-editor">
      <div className="flex flex-wrap items-start gap-6">
        <div className="text-center">
          <div className="relative aspect-[4/5] w-32 overflow-hidden rounded-lg bg-slate-100">
            {photoSrc ? (
              <img src={photoSrc} alt="Profile" className="absolute inset-0 h-full w-full object-cover object-top" data-testid="profile-photo-preview" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-navy font-display text-3xl text-gold">{(profile.full_name || "?").charAt(0)}</div>
            )}
          </div>
          <button onClick={() => photoRef.current?.click()} disabled={uploading} data-testid="profile-photo-upload-button"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-[10px] font-bold text-white hover:bg-navy-800 disabled:opacity-60">
            <UploadCloud className="h-3 w-3" /> {uploading ? "Uploading..." : "Upload Photo"}
          </button>
          <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="profile-photo-input" onChange={(e) => uploadPhoto(e.target.files[0])} />
        </div>
        <div className="grid flex-1 gap-4 sm:grid-cols-2">
          {PROFILE_TEXT.map(([k, label, type]) => (
            <div key={k}>
              <label className="field-label">{label}</label>
              <input type={type || "text"} className={inputCls} value={profile[k] ?? ""} onChange={set(k)} data-testid={`profile-${k}`} />
            </div>
          ))}
          {PROFILE_LISTS.map(([k, label]) => (
            <div key={k}>
              <label className="field-label">{label} (comma separated)</label>
              <input className={inputCls} value={Array.isArray(profile[k]) ? profile[k].join(", ") : profile[k] ?? ""} onChange={set(k)} data-testid={`profile-${k}`} />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 grid gap-4">
        {PROFILE_AREAS.map(([k, label]) => (
          <div key={k}>
            <label className="field-label">{label}</label>
            <textarea rows={2} className={inputCls} value={profile[k] ?? ""} onChange={set(k)} data-testid={`profile-${k}`} />
          </div>
        ))}
      </div>
      {offersResearch && (
        <div className="mt-4 grid gap-4 rounded-lg bg-gold-soft/60 p-4 sm:grid-cols-2" data-testid="profile-research-fields">
          {RESEARCH_TEXT.map(([k, label]) => (
            <div key={k}>
              <label className="field-label">{label}</label>
              <input className={inputCls} value={profile[k] ?? ""} onChange={set(k)} data-testid={`profile-${k}`} />
            </div>
          ))}
          {RESEARCH_LISTS.map(([k, label]) => (
            <div key={k}>
              <label className="field-label">{label} (comma separated)</label>
              <input className={inputCls} value={Array.isArray(profile[k]) ? profile[k].join(", ") : profile[k] ?? ""} onChange={set(k)} data-testid={`profile-${k}`} />
            </div>
          ))}
        </div>
      )}
      <button onClick={save} disabled={saving} data-testid="profile-save-button"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3 text-sm font-bold text-white hover:bg-navy-800 disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Profile"}
      </button>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { lang } = useLanguage();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [cells, setCells] = useState(new Set());
  const [timezone, setTimezone] = useState("UTC");
  const [durations, setDurations] = useState([60]);
  const [rate, setRate] = useState(50);
  const [researchRate, setResearchRate] = useState(50);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlocked, setNewBlocked] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    expertApi.get("/expert-portal/me").then((r) => {
      setMe(r.data);
      const a = r.data.availability || {};
      let ranges = a.schedule || [];
      if (!ranges.length && a.days?.length) {
        const legacy = (a.slots || []).slice().sort();
        const start = legacy[0] || "09:00";
        let end = "17:00";
        if (legacy.length) {
          const [h, m] = legacy[legacy.length - 1].split(":").map(Number);
          end = h + 1 > 23 ? "23:59" : `${String(h + 1).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        }
        ranges = a.days.map((d) => ({ day: d, start, end }));
      }
      setCells(scheduleToCells(ranges));
      setTimezone(a.timezone || "UTC");
      setDurations(a.durations?.length ? a.durations : [60]);
      setBlockedDates(a.blocked_dates || []);
      setRate(r.data.hourly_rate ?? 50);
      setResearchRate(r.data.research_hourly_rate ?? 50);
    }).catch(() => onLogout());
    expertApi.get("/expert-portal/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const toggleDuration = (d) => setDurations(durations.includes(d) ? durations.filter((x) => x !== d) : [...durations, d]);
  const offersResearch = me && ["research", "both"].includes(me.consultation_type);

  const save = async () => {
    setSaving(true);
    try {
      await expertApi.put("/expert-portal/availability", {
        schedule: cellsToSchedule(cells), timezone, durations, blocked_dates: blockedDates,
        hourly_rate: parseFloat(rate) || 50,
        research_hourly_rate: parseFloat(researchRate) || 50,
      });
      toast.success("Availability saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!me) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading...</div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.date >= today && !["cancelled", "completed", "no-show"].includes(b.booking_status));
  const history = bookings.filter((b) => !upcoming.includes(b));

  const Table = ({ rows, testid }) => (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[900px] text-sm" data-testid={testid}>
        <thead>
          <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
            {["Booking ID", "Client", "Date & Time", "Duration", "Total", "Status", "Meeting link & Files"].map((h) => (
              <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => <BookingRow key={b.id} b={b} onSaved={load} />)}
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No bookings.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100" data-testid="expert-portal-dashboard">
      <header className="flex h-16 items-center justify-between bg-navy-950 px-5">
        <Logo dark compact />
        <div className="flex items-center gap-4">
          <LangToggle className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-gold" />
          <span className="text-sm text-slate-300">{me.full_name}</span>
          <button onClick={onLogout} data-testid="expert-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-bold text-white hover:border-gold">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-3xl font-semibold text-navy">My Profile</h1>
        <p className="mt-1 text-sm text-slate-500">Keep your professional profile up to date. The Stratedge UK team reviews updates before directory publication.</p>
        <div className="mt-6"><ProfileEditor me={me} onSaved={load} /></div>

        <h1 className="mt-12 font-display text-3xl font-semibold text-navy">Consultation Availability</h1>
        <p className="mt-1 text-sm text-slate-500">Set when clients can book paid consultations with you.</p>

        <div className="mt-8 rounded-lg border border-slate-200 bg-white p-7 shadow-sm" data-testid="availability-editor">
          <label className="field-label">Weekly availability grid</label>
          <AvailabilityGrid cells={cells} onChange={setCells} lang={lang} />

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <label className="field-label">Time zone</label>
              <select className={inputCls} value={timezone} onChange={(e) => setTimezone(e.target.value)} data-testid="avail-timezone-select">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Expert consultation rate (USD/hour)</label>
              <input type="number" min="1" className={inputCls} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="avail-rate-input" />
            </div>
            {offersResearch && (
              <div>
                <label className="field-label">Research consultation rate (USD/hour)</label>
                <input type="number" min="1" className={inputCls} value={researchRate} onChange={(e) => setResearchRate(e.target.value)} data-testid="avail-research-rate-input" />
              </div>
            )}
          </div>

          <label className="field-label mt-6">Consultation duration options</label>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => (
              <button key={d} type="button" onClick={() => toggleDuration(d)} data-testid={`avail-duration-${d}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${durations.includes(d) ? "bg-gold text-navy" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {d} min
              </button>
            ))}
          </div>

          <label className="field-label mt-6">Temporarily blocked dates (clients cannot book these)</label>
          <div className="flex flex-wrap items-center gap-3">
            <input type="date" className={inputCls} style={{ maxWidth: 200 }} value={newBlocked} onChange={(e) => setNewBlocked(e.target.value)} data-testid="blocked-date-input" />
            <button type="button" data-testid="blocked-date-add"
              onClick={() => { if (newBlocked && !blockedDates.includes(newBlocked)) { setBlockedDates([...blockedDates, newBlocked].sort()); setNewBlocked(""); } }}
              className="rounded-full bg-slate-100 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-200">
              Block Date
            </button>
          </div>
          {blockedDates.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="blocked-dates-list">
              {blockedDates.map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
                  {d}
                  <button type="button" onClick={() => setBlockedDates(blockedDates.filter((x) => x !== d))} data-testid={`blocked-date-remove-${d}`} aria-label="Remove"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}

          <button onClick={save} disabled={saving} data-testid="availability-save-button"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3 text-sm font-bold text-navy hover:bg-gold-hover disabled:opacity-60">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Availability"}
          </button>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold text-navy">Upcoming Consultations</h2>
        <div className="mt-4"><Table rows={upcoming} testid="expert-bookings-upcoming" /></div>

        <h2 className="mt-12 font-display text-2xl font-semibold text-navy">Booking History</h2>
        <div className="mt-4"><Table rows={history} testid="expert-bookings-history" /></div>
      </main>
    </div>
  );
}

export default function ExpertPortal() {
  const [token, setToken] = useState(localStorage.getItem("stratedge-expert-token"));
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("reset_token");
  const resetEmail = params.get("email");
  if (resetToken && resetEmail) {
    return (
      <ResetPassword
        email={resetEmail}
        token={resetToken}
        onDone={() => { window.history.replaceState({}, "", "/expert-portal"); window.location.reload(); }}
      />
    );
  }
  if (!token) return <Login onSuccess={(tok) => setToken(tok)} />;
  return <Dashboard onLogout={() => { localStorage.removeItem("stratedge-expert-token"); setToken(null); }} />;
}
